/**
 * One method per endpoint, grouped by the server area it calls. Self endpoints
 * never take a user id: the server acts on the authenticated user only.
 */

import type { HttpTransport } from '@dealr/transport';
import { withQuery } from '@dealr/transport';
import type { ConfirmOutcome } from '@dealr/transport';
import { answerConfirms, send, sendConfirmable, type ConfirmPort } from './send';
import {
  ack,
  backupCodes,
  businessSchedule,
  challenge,
  forgotUsername,
  googleConnection,
  identityEnrollment,
  loginSuccess,
  loginVerify,
  lostDevice,
  methodCreated,
  microsoftConnection,
  oauthConnections,
  recoveryRequestResponse,
  recoveryRequestsResponse,
  resetIdentityCheck,
  resetLostDevice,
  resetVerification,
  securityStatus,
  sessionApprovals,
  trustedDevices,
  stepUpStatus,
  totpSetup,
  unread,
  userDevices,
  userSettings,
  webAuthnOptions,
  whatsNewSubscriptions,
} from './schemas/account';
import type {
  BackupCodesResponse,
  BusinessSchedule,
  BusinessScheduleSave,
  ChallengeResponse,
  GoogleConnection,
  LoginBody,
  LoginMfaPendingBody,
  LoginMfaVerifyBody,
  LoginSuccessBody,
  MethodCreatedResponse,
  MicrosoftConnection,
  OauthConnection,
  UserDevice,
  RecoveryApprovalRole,
  RecoveryRequestCreate,
  RecoveryRequestResponse,
  RecoveryRequestsResponse,
  ResetPasswordBody,
  ResetVerificationResponse,
  SessionApprovalsResponse,
  TrustedDevicesResponse,
  SecurityStatus,
  StepUpProof,
  SuccessResponse,
  TimeOffBody,
  TotpSetupResponse,
  UserSettingsRow,
  WebAuthnJson,
  WebAuthnOptionsResponse,
  WhatsNewSubscriptions,
} from './types';

const SECURITY = '/api/account-security';

function enc(value: string | number): string {
  return encodeURIComponent(String(value));
}

export interface AccountSecurityApi {
  status(): Promise<SecurityStatus>;
  /** Adding a method beside a verified one needs `stepUp` from an existing factor; the first needs none. */
  totpSetup(stepUp?: StepUpProof): Promise<TotpSetupResponse>;
  totpConfirm(body: { method_uuid: string; code: string; label?: string }): Promise<SuccessResponse>;
  webauthnRegisterOptions(stepUp?: StepUpProof): Promise<WebAuthnOptionsResponse>;
  webauthnRegisterVerify(body: { credential: WebAuthnJson; label?: string }): Promise<MethodCreatedResponse>;
  addPhone(body: { type: 'sms' | 'voice'; phone: string; step_up?: StepUpProof }): Promise<MethodCreatedResponse>;
  confirmPhone(body: { method_uuid: string; code: string }): Promise<SuccessResponse>;
  removeMethod(methodUuid: string, stepUp: StepUpProof): Promise<SuccessResponse>;
  /** `action` names the change for another-device approval ("… wants to change your password."). */
  stepUpChallenge(methodUuid: string, action?: string): Promise<ChallengeResponse & { approval_token?: string; pending?: boolean }>;
  /** Another-device step-up: `{status: pending|approved|denied|none}`; does not spend the approval. */
  stepUpStatus?(approvalToken: string): Promise<{ status: 'pending' | 'approved' | 'denied' | 'none' | string }>;
  /** `account-security`: the signed-in user's own MFA-reset request. */
  recoveryRequest?(): Promise<LostDeviceResponse>;
  /** Rename a two-step method: `PATCH /mfa/:uuid {label}` (1-60 characters). */
  renameMethod?(methodUuid: string, label: string): Promise<SuccessResponse>;
  /** `account-security`: start self-enrollment (step-up required). `in_app` answers a capture token. */
  identityEnrollment?(body: { step_up: StepUpProof; channel?: 'in_app' | 'email' | 'sms' }): Promise<IdentityEnrollmentStart>;
  /** `step_up` is omitted only when the user has no method to step up with; the server decides whether that is allowed. */
  changePassword(body: { current_password: string; new_password: string; step_up?: StepUpProof }): Promise<SuccessResponse>;
  /** `account-security`: replaces every earlier code; the response is the only time the codes are shown. */
  regenerateBackupCodes(stepUp: StepUpProof): Promise<BackupCodesResponse>;
  /** `account-security` (404 when off): sign-ins on another device waiting for this user. */
  sessionApprovals(): Promise<SessionApprovalsResponse>;
  /** `match` is the number picked from the prompt's choices; a wrong one denies it (403 `wrong_number`). */
  approveSessionApproval(approvalUuid: string, stepUp: StepUpProof, match: number): Promise<SuccessResponse>;
  denySessionApproval(approvalUuid: string): Promise<SuccessResponse>;
  /** `account-security` (404 when off): the user's own remembered browsers. */
  trustedDevices?(): Promise<TrustedDevicesResponse>;
  forgetTrustedDevice?(deviceUuid: string): Promise<SuccessResponse>;
  forgetAllTrustedDevices?(): Promise<SuccessResponse>;
}

export function createAccountSecurityApi(transport: HttpTransport): AccountSecurityApi {
  return {
    status: () => send(transport, { method: 'GET', path: `${SECURITY}/status` }, securityStatus),
    totpSetup: (stepUp) => send(transport, { method: 'POST', path: `${SECURITY}/mfa/totp/setup`, body: stepUp ? { step_up: stepUp } : {} }, totpSetup),
    totpConfirm: (body) => send(transport, { method: 'POST', path: `${SECURITY}/mfa/totp/confirm`, body }, ack),
    webauthnRegisterOptions: (stepUp) =>
      send(transport, { method: 'POST', path: `${SECURITY}/mfa/webauthn/register/options`, body: stepUp ? { step_up: stepUp } : {} }, webAuthnOptions),
    webauthnRegisterVerify: (body) =>
      send(transport, { method: 'POST', path: `${SECURITY}/mfa/webauthn/register/verify`, body }, methodCreated),
    addPhone: (body) => send(transport, { method: 'POST', path: `${SECURITY}/mfa/phone`, body }, methodCreated),
    confirmPhone: (body) => send(transport, { method: 'POST', path: `${SECURITY}/mfa/phone/confirm`, body }, ack),
    removeMethod: (methodUuid, stepUp) =>
      send(transport, { method: 'DELETE', path: `${SECURITY}/mfa/${enc(methodUuid)}`, body: { step_up: stepUp } }, ack),
    stepUpChallenge: (methodUuid, action) =>
      send(transport, { method: 'POST', path: `${SECURITY}/step-up/challenge`, body: action ? { method_uuid: methodUuid, action } : { method_uuid: methodUuid } }, challenge),
    stepUpStatus: (approvalToken) => send(transport, { method: 'POST', path: `${SECURITY}/step-up/status`, body: { approval_token: approvalToken } }, stepUpStatus),
    recoveryRequest: () => send(transport, { method: 'POST', path: `${SECURITY}/recovery-request`, body: {} }, lostDevice),
    renameMethod: (methodUuid, label) => send(transport, { method: 'PATCH', path: `${SECURITY}/mfa/${enc(methodUuid)}`, body: { label } }, ack),
    identityEnrollment: (body) => send(transport, { method: 'POST', path: `${SECURITY}/identity/enrollment`, body }, identityEnrollment),
    changePassword: (body) => send(transport, { method: 'POST', path: `${SECURITY}/password`, body }, ack),
    regenerateBackupCodes: (stepUp) =>
      send(transport, { method: 'POST', path: `${SECURITY}/backup-codes`, body: { step_up: stepUp } }, backupCodes),
    sessionApprovals: () => send(transport, { method: 'GET', path: `${SECURITY}/approvals` }, sessionApprovals),
    approveSessionApproval: (approvalUuid, stepUp, match) =>
      send(transport, { method: 'POST', path: `${SECURITY}/approvals/${enc(approvalUuid)}/approve`, body: { step_up: stepUp, match } }, ack),
    denySessionApproval: (approvalUuid) =>
      send(transport, { method: 'POST', path: `${SECURITY}/approvals/${enc(approvalUuid)}/deny`, body: {} }, ack),
    trustedDevices: () => send(transport, { method: 'GET', path: `${SECURITY}/trusted-devices` }, trustedDevices),
    forgetTrustedDevice: (deviceUuid) => send(transport, { method: 'DELETE', path: `${SECURITY}/trusted-devices/${enc(deviceUuid)}` }, ack),
    forgetAllTrustedDevices: () => send(transport, { method: 'DELETE', path: `${SECURITY}/trusted-devices` }, ack),
  };
}

/** The second-factor step of `POST /api/login`, authorized by the `mfa_token` the 401 carried. */
export interface LoginMfaApi {
  challenge(body: { mfa_token: string; method_uuid: string }): Promise<ChallengeResponse>;
  /** `account-security`: for `method_uuid:'session'` a 202 `{pending:true}` means keep polling. */
  verify(body: LoginMfaVerifyBody): Promise<LoginSuccessBody | LoginMfaPendingBody>;
  /**
   * Lost device: `POST /login/mfa/recovery {mfa_token, option}`. Answers
   * `{recovery_request: "started"}` or `{identity_check: {status, capture_token}}`.
   */
  recovery?(body: { mfa_token: string; option: 'identity_check' | 'recovery_request' }): Promise<LostDeviceResponse>;
}

/** `POST /account-security/identity/enrollment`. */
export interface IdentityEnrollmentStart {
  success?: boolean;
  enrollment: { status: string; channel: string; delivered_to: string | null; capture_token: string | null };
}

/** A lost-device call's answer (login, reset link, signed-in recovery request). */
export interface LostDeviceResponse {
  success?: boolean;
  recovery_request?: 'started';
  identity_check?: { status: string; capture_token: string | null } | string;
  capture_token?: string | null;
  requirement?: string;
}

/** `POST /api/login`. A 401 with `mfa_required` and a 406 (emailed device code) reject; core reads them. */
export interface LoginApi {
  login(body: LoginBody): Promise<LoginSuccessBody>;
}

/**
 * `confirm` answers the pre-auth prompt `/api/login` can raise (400 with
 * `x-client-confirm-dialog`: the session limit, "User Already Logged In") and
 * the request is retried with the answer, as main.js `handleLoginConfirmDialog`
 * does; without it the prompt rejects with the 400.
 */
export function createLoginApi(transport: HttpTransport, confirm?: ConfirmPort): LoginApi {
  return {
    login: (body) => answerConfirms(sendConfirmable(transport, { method: 'POST', path: '/api/login', body }, loginSuccess), confirm),
  };
}

/** `confirm` answers the session-limit prompt the verify step can raise; without it the prompt rejects. */
export function createLoginMfaApi(transport: HttpTransport, confirm?: ConfirmPort): LoginMfaApi {
  return {
    challenge: (body) => send(transport, { method: 'POST', path: '/api/login/mfa/challenge', body }, challenge),
    verify: (body) =>
      answerConfirms(sendConfirmable(transport, { method: 'POST', path: '/api/login/mfa/verify', body }, loginVerify), confirm),
    recovery: (body) => send(transport, { method: 'POST', path: '/api/login/mfa/recovery', body }, lostDevice),
  };
}

/**
 * Staff actions on another user's record (`/api/users/:userID/...`). The
 * reset sends the link to the email on file; under `account-security-enforcement`
 * that is the only staff reset (tier 1), and contact changes go through
 * recovery requests.
 */
export interface StaffVerificationApi {
  resetPassword(userId: number | string): Promise<unknown>;
}

export function createStaffVerificationApi(transport: HttpTransport): StaffVerificationApi {
  return {
    resetPassword: (userId) => send(transport, { method: 'POST', path: `/api/users/${enc(userId)}/reset`, body: {} }, unread),
  };
}

/**
 * Tiered recovery (`account-security`; every route 404s when off):
 * the dealership-admin MFA reset, recovery requests and their approvals.
 */
export interface RecoveryApi {
  resetMfa(userId: number | string, stepUp: StepUpProof): Promise<RecoveryRequestResponse>;
  createRequest(userId: number | string, body: RecoveryRequestCreate): Promise<RecoveryRequestResponse>;
  list(status?: 'open' | 'all'): Promise<RecoveryRequestsResponse>;
  get(requestUuid: string): Promise<RecoveryRequestResponse>;
  approve(requestUuid: string, body: { role: RecoveryApprovalRole; step_up: StepUpProof; note?: string }): Promise<RecoveryRequestResponse>;
  deny(requestUuid: string, note?: string): Promise<RecoveryRequestResponse>;
  cancel(requestUuid: string): Promise<RecoveryRequestResponse>;
  sendIdentityCheck(requestUuid: string, channel: 'email' | 'sms'): Promise<RecoveryRequestResponse>;
  /** A dealership admin confirms the user's identity instead of the ID check (tier 3; note required). */
  confirmIdentity(requestUuid: string, body: { step_up: StepUpProof; note: string }): Promise<RecoveryRequestResponse>;
  /**
   * `account-security`: another admin (or a Dealr security
   * approver) confirms a privileged user's identity instead of an ID check.
   */
  confirmIdentityRequirement(userId: number | string, body: { step_up: StepUpProof; note: string }): Promise<SuccessResponse>;
}

export function createRecoveryApi(transport: HttpTransport): RecoveryApi {
  const request = (uuid: string) => `/api/recovery-requests/${enc(uuid)}`;
  return {
    resetMfa: (userId, stepUp) =>
      send(transport, { method: 'POST', path: `/api/users/${enc(userId)}/mfa/reset`, body: { step_up: stepUp } }, recoveryRequestResponse),
    createRequest: (userId, body) =>
      send(transport, { method: 'POST', path: `/api/users/${enc(userId)}/recovery-requests`, body }, recoveryRequestResponse),
    list: (status = 'open') => send(transport, { method: 'GET', path: `/api/recovery-requests?status=${enc(status)}` }, recoveryRequestsResponse),
    get: (uuid) => send(transport, { method: 'GET', path: request(uuid) }, recoveryRequestResponse),
    approve: (uuid, body) => send(transport, { method: 'POST', path: `${request(uuid)}/approve`, body }, recoveryRequestResponse),
    deny: (uuid, note) => send(transport, { method: 'POST', path: `${request(uuid)}/deny`, body: note ? { note } : {} }, recoveryRequestResponse),
    cancel: (uuid) => send(transport, { method: 'POST', path: `${request(uuid)}/cancel`, body: {} }, recoveryRequestResponse),
    sendIdentityCheck: (uuid, channel) =>
      send(transport, { method: 'POST', path: `${request(uuid)}/identity-check`, body: { channel } }, recoveryRequestResponse),
    confirmIdentity: (uuid, body) =>
      send(transport, { method: 'POST', path: `${request(uuid)}/confirm-identity`, body }, recoveryRequestResponse),
    confirmIdentityRequirement: (userId, body) =>
      send(transport, { method: 'POST', path: `/api/users/${enc(userId)}/identity-requirement/confirm`, body }, ack),
  };
}

/** The unauthenticated reset-link endpoints, authorized by the emailed token alone. */
export interface ResetLinkApi {
  /** `release-access`: emails a reset link for the username (the server answers with a message either way). */
  requestLink(username: string): Promise<unknown>;
  /** What the link needs before a new password; 404 while the verification step is off. */
  verification(token: string): Promise<ResetVerificationResponse>;
  verificationMfaChallenge(token: string, methodUuid: string): Promise<ChallengeResponse>;
  verificationMfaVerify(token: string, proof: StepUpProof): Promise<SuccessResponse>;
  /** Lost device on a reset link: `{token, lost_device}`; answers a capture token or `recovery_request: "started"`. */
  verificationLostDevice(token: string, option: 'identity_check' | 'recovery_request'): Promise<LostDeviceResponse & ResetVerificationResponse>;
  /** Saves the new password; success signs the user in (the normal `/api/login` body). */
  setPassword(body: ResetPasswordBody): Promise<LoginSuccessBody>;
  /** `account-security`: starts (first call) or reports the ID + face check for a reset link. */
  identityCheck(token: string): Promise<SuccessResponse & { identity_check?: string }>;
  /** "This wasn't me": freezes the account named by a recovery email's token. */
  securityFreeze(token: string): Promise<SuccessResponse>;
  /** `release-access`: sends the username(s) on file to an email or phone. Answers `{sent: true}` whether or not one matched. */
  forgotUsername(contact: { email: string } | { phone: string }): Promise<{ sent?: boolean }>;
}

export function createResetLinkApi(transport: HttpTransport): ResetLinkApi {
  const verify = '/api/changePass/verification';
  return {
    requestLink: (username) => send(transport, { method: 'POST', path: '/api/forgotPassword', body: { username } }, unread),
    verification: (token) => send(transport, { method: 'POST', path: verify, body: { token } }, resetVerification),
    verificationMfaChallenge: (token, methodUuid) =>
      send(transport, { method: 'POST', path: `${verify}/mfa/challenge`, body: { token, method_uuid: methodUuid } }, challenge),
    verificationMfaVerify: (token, proof) => send(transport, { method: 'POST', path: `${verify}/mfa/verify`, body: { token, ...proof } }, ack),
    verificationLostDevice: (token, option) => send(transport, { method: 'POST', path: verify, body: { token, lost_device: option } }, resetLostDevice),
    setPassword: (body) => send(transport, { method: 'POST', path: '/api/changePass', body }, loginSuccess),
    identityCheck: (token) => send(transport, { method: 'POST', path: '/api/changePass/verification/identity', body: { token } }, resetIdentityCheck),
    securityFreeze: (token) => send(transport, { method: 'POST', path: '/api/changePass/security-freeze', body: { token } }, ack),
    forgotUsername: (contact) => send(transport, { method: 'POST', path: '/api/access/forgot-username', body: contact }, forgotUsername),
  };
}

/** The endpoints the settings page has always called. */
export interface AccountSettingsApi {
  userSettings(): Promise<UserSettingsRow>;
  /** The user editor's handler can raise a confirm prompt (seat charges); `'confirm'` here means it was declined. */
  saveProfile(userId: number | string, row: UserSettingsRow): Promise<ConfirmOutcome<unknown>>;
  googleConnection(): Promise<GoogleConnection>;
  googleDisconnect(): Promise<unknown>;
  /** release-office365-integration. */
  microsoftConnection(): Promise<MicrosoftConnection>;
  microsoftDisconnect(): Promise<unknown>;
  oauthConnections(): Promise<OauthConnection[] | null>;
  oauthRevoke(connectionId: number | string): Promise<unknown>;
  /** The native-app sessions signed in as this user, as the legacy page's Devices list. */
  userDevices(userId: number | string): Promise<UserDevice[] | null>;
  /** Signs that app out and stops its pushes. */
  logoutDevice(userId: number | string, deviceId: number | string): Promise<unknown>;
  carfaxLogout(): Promise<unknown>;
  whatsNewSubscriptions(): Promise<WhatsNewSubscriptions>;
  saveWhatsNewSubscriptions(unsubscribed: string[]): Promise<unknown>;
  businessSchedule(dealerId?: number | null): Promise<BusinessSchedule>;
  saveBusinessSchedule(body: BusinessScheduleSave): Promise<BusinessSchedule>;
  removeBusinessSchedule(userId: number, dealerId: number): Promise<BusinessSchedule>;
  addTimeOff(body: TimeOffBody): Promise<unknown>;
  deleteTimeOff(exceptionUuid: string): Promise<unknown>;
}

const HOURS = '/api/schedule/business-hours';

export function createAccountSettingsApi(transport: HttpTransport): AccountSettingsApi {
  return {
    userSettings: () => send(transport, { method: 'GET', path: '/api/users/userSettings' }, userSettings),
    saveProfile: (userId, row) => sendConfirmable(transport, { method: 'PATCH', path: `/api/users/${enc(userId)}`, body: row }, unread),
    googleConnection: () => send(transport, { method: 'GET', path: '/api/google/connection' }, googleConnection),
    googleDisconnect: () => send(transport, { method: 'POST', path: '/api/google/disconnect', body: {} }, unread),
    microsoftConnection: () => send(transport, { method: 'GET', path: '/api/microsoft/connection' }, microsoftConnection),
    microsoftDisconnect: () => send(transport, { method: 'POST', path: '/api/microsoft/disconnect', body: {} }, unread),
    oauthConnections: () => send(transport, { method: 'GET', path: '/api/oauth/connections' }, oauthConnections),
    oauthRevoke: (connectionId) =>
      send(transport, { method: 'POST', path: '/api/oauth/revoke', body: { oauth_access_token_id: connectionId } }, unread),
    userDevices: (userId) => send(transport, { method: 'GET', path: `/api/users/${enc(userId)}/devices` }, userDevices),
    logoutDevice: (userId, deviceId) => send(transport, { method: 'DELETE', path: `/api/users/${enc(userId)}/devices/${enc(deviceId)}` }, unread),
    carfaxLogout: () => send(transport, { method: 'POST', path: '/api/inventory/CarFax/logout', body: {} }, unread),
    whatsNewSubscriptions: () => send(transport, { method: 'GET', path: '/api/whatsNew/subscriptions' }, whatsNewSubscriptions),
    saveWhatsNewSubscriptions: (unsubscribed) =>
      send(transport, { method: 'POST', path: '/api/whatsNew/subscriptions', body: { unsubscribed } }, unread),
    businessSchedule: (dealerId) =>
      send(transport, {
        method: 'GET',
        path: '/api/users/business-schedule' + (dealerId ? `?dealer_id=${enc(dealerId)}` : ''),
      }, businessSchedule),
    saveBusinessSchedule: (body) => send(transport, { method: 'PUT', path: '/api/users/business-schedule', body }, businessSchedule),
    removeBusinessSchedule: (userId, dealerId) =>
      send(transport, { method: 'DELETE', path: withQuery(`${HOURS}/employee-schedule`, { user_id: userId, dealer_id: dealerId }) }, businessSchedule),
    addTimeOff: (body) => send(transport, { method: 'POST', path: `${HOURS}/exceptions`, body }, unread),
    deleteTimeOff: (exceptionUuid) => send(transport, { method: 'DELETE', path: `${HOURS}/exceptions/${enc(exceptionUuid)}` }, unread),
  };
}

export interface AccountApi {
  security: AccountSecurityApi;
  login: LoginMfaApi;
  staff: StaffVerificationApi;
  settings: AccountSettingsApi;
  recovery: RecoveryApi;
  resetLink: ResetLinkApi;
}

export function createAccountApi(transport: HttpTransport, options: { confirmLogin?: ConfirmPort } = {}): AccountApi {
  return {
    security: createAccountSecurityApi(transport),
    login: createLoginMfaApi(transport, options.confirmLogin),
    staff: createStaffVerificationApi(transport),
    settings: createAccountSettingsApi(transport),
    recovery: createRecoveryApi(transport),
    resetLink: createResetLinkApi(transport),
  };
}
